import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Bell, ChevronLeft, Settings, ClipboardList, Activity, HelpCircle, Star, QrCode, CheckCheck, Users2 } from "lucide-react";
import { eventosApi, tareasApi, encuestasApi, walkinApi, type EncuestaQrActiva, type WalkinNotificacion } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL, puedeVerEncuestaQr } from "../lib/permisos";
import { HelpDrawer } from "./HelpDrawer";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../lib/branding";
import { EncuestaQrGrandeModal } from "./EncuestaQrGrandeModal";

interface Notif {
  id: number;
  tipo: "llamar_mesonero" | "solicitar_cuenta";
  estado: "pendiente" | "visto" | "resuelto";
  clienteNombre?: string;
  mesaNumero?: number;
  createdAt: string;
}

interface NotifInterna {
  id: number;
  tipo: string;
  titulo: string;
  mensaje?: string;
  link?: string;
  createdAt: string;
}

interface Branding {
  colorPrimario?: string;
  colorSecundario?: string;
  logoUrl?: string | null;
  descripcionTienda?: string | null;
  sucursalNombre?: string | null;
}

const ROUTE_LABELS: Record<string, string> = {
  "/tienda/menu-config": "Configuración",
  "/tienda/bloqueos":    "Bloqueos",
  "/reservas":           "Reservas",
  "/calendario":         "Calendario",
  "/reportes":           "Reportes",
  "/customers":          "Clientes",
  "/cliente360":         "Cliente 360°",
  "/audiencias":         "Audiencias",
  "/usuarios":           "Usuarios",
  "/sucursales":         "Sucursales",
};

export function TiendaLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [notifs, setNotifs] = useState<Notif[]>([]);
  const [open, setOpen] = useState(false);
  const [encuestasQr, setEncuestasQr] = useState<EncuestaQrActiva[]>([]);
  const [notifsInternas, setNotifsInternas] = useState<NotifInterna[]>([]);
  const [walkinEsperando, setWalkinEsperando] = useState<WalkinNotificacion[]>([]);
  const [encuestaQrAbierta, setEncuestaQrAbierta] = useState<EncuestaQrActiva | null>(null);
  // Contador de tareas pendientes (checklist eventos + avisos operativos)
  const [tareasCount, setTareasCount] = useState(0);
  const [branding, setBranding] = useState<Branding>({});
  const [scrolled, setScrolled] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const isDashboard = location.pathname === "/tienda";
  const pageLabel = ROUTE_LABELS[location.pathname] ?? "";
  const settingsActive = location.pathname.startsWith("/tienda/menu-config");

  // Cargar contador de tareas pendientes (se refresca cada 2 min)
  useEffect(() => {
    if (!token) return;
    const cargar = () => {
      Promise.all([
        eventosApi.misTareas?.().catch(() => []),
        tareasApi.mias().catch(() => []),
      ]).then(([checklist, avisos]) => {
        const total = (Array.isArray(checklist) ? checklist.length : 0)
                    + (Array.isArray(avisos) ? avisos.length : 0);
        setTareasCount(total);
      });
    };
    cargar();
    const interval = setInterval(cargar, 120_000); // refresca cada 2 min
    return () => clearInterval(interval);
  }, [token]);

  // Cargar branding del cliente. Si el usuario está restringido a UNA sola
  // sucursal (cajero/mesonero/etc. asignado puntualmente), usar la de esa
  // sucursal -- no la "1" (cabecera del grupo) a secas, que es lo que veía
  // cualquier usuario sin importar a cuál sucursal estuviera asignado.
  const sucursalIdBranding = user?.sucursalIds?.length === 1 ? user.sucursalIds[0] : 1;
  useEffect(() => {
    fetch(`/api/v1/menu-publico/sucursal/${sucursalIdBranding}/config`)
      .then(r => r.json())
      .then(d => {
        setBranding({
          colorPrimario:  d.colorPrimario,
          colorSecundario: d.colorSecundario,
          logoUrl:        d.logoUrl,
          descripcionTienda: d.descripcionTienda,
          sucursalNombre: d.sucursalNombre,
        });
        aplicarFaviconPagina(d.logoUrl);
        aplicarTituloPagina(d.sucursalNombre ?? d.descripcionTienda?.split(".")[0]?.trim());
      })
      .catch(() => {});
  }, [sucursalIdBranding]);

  // Efecto de scroll para el header
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const fetchPendientes = async () => {
    if (!token) return;
    try {
      const res = await fetch("/api/v1/menu-notifications/1/pendientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setNotifs(await res.json());
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchPendientes();
    const id = setInterval(fetchPendientes, 5000);
    return () => clearInterval(id);
  }, [token]);

  const fetchNotifsInternas = async () => {
    if (!token) return;
    try {
      const res = await fetch("/api/v1/notificaciones/pendientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setNotifsInternas(await res.json());
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifsInternas();
    const id = setInterval(fetchNotifsInternas, 20_000);
    return () => clearInterval(id);
  }, [token]);

  const marcarNotifInterna = async (id: number) => {
    setNotifsInternas(prev => prev.filter(n => n.id !== id));
    await fetch(`/api/v1/notificaciones/${id}/vista`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  const marcarTodasNotifsInternas = async () => {
    setNotifsInternas([]);
    await fetch(`/api/v1/notificaciones/marcar-todas-vistas`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  // Walk-in esperando (fila del QR de la puerta)
  useEffect(() => {
    if (!token) return;
    const cargar = () => {
      walkinApi.notificaciones().then(setWalkinEsperando).catch(() => {});
    };
    cargar();
    const id = setInterval(cargar, 15_000);
    return () => clearInterval(id);
  }, [token]);

  // Encuestas QR activas: personal con contacto directo con el cliente
  // (cajero, operativo/mesonero, gerencia...) para ofrecerle el QR al cliente.
  useEffect(() => {
    if (!token || !puedeVerEncuestaQr(user)) return;
    const cargar = () => {
      encuestasApi.listarActivasQr().then(setEncuestasQr).catch(() => setEncuestasQr([]));
    };
    cargar();
    const id = setInterval(cargar, 60_000);
    return () => clearInterval(id);
  }, [token, user?.role]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const marcar = async (id: number, accion: "visto" | "resuelto") => {
    await fetch(`/api/v1/menu-notifications/${id}/${accion}`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}` },
    });
    fetchPendientes();
  };

  const pendientes = notifs.filter(n => n.estado === "pendiente");
  const color = branding.colorPrimario ?? "#1a1a1a";
  const restaurantName = branding.sucursalNombre ?? branding.descripcionTienda?.split(".")?.[0] ?? "Panel";

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%)" }}
    >
      {/* ── HEADER PREMIUM ── */}
      <header
        className={`sticky top-0 z-40 transition-all duration-200 ${
          scrolled
            ? "shadow-lg bg-white/95 backdrop-blur-md"
            : "bg-white/80 backdrop-blur-sm shadow-sm"
        }`}
        style={{ borderBottom: `1px solid ${color}20` }}
      >
        {/* Línea de acento superior */}
        <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${branding.colorSecundario ?? '#6366f1'})` }} />

        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-center gap-3 h-16">

            {/* Back / Logo */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {!isDashboard && (
                <Link
                  to="/tienda"
                  className="flex items-center justify-center w-8 h-8 rounded-lg text-white transition-all hover:opacity-80 active:scale-95 shrink-0"
                  style={{ background: color }}
                >
                  <ChevronLeft className="size-4" />
                </Link>
              )}

              {/* Logo del cliente */}
              <Link to="/tienda" className="flex items-center gap-2.5 min-w-0">
                {branding.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt={restaurantName}
                    className="h-9 w-9 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
                  />
                ) : (
                  <div
                    className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm shrink-0"
                    style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
                  >
                    {restaurantName[0]?.toUpperCase() ?? "R"}
                  </div>
                )}
                <div className="min-w-0 hidden sm:block">
                  <p className="font-semibold text-sm text-gray-900 leading-tight truncate">
                    {restaurantName}
                  </p>
                  {!isDashboard && pageLabel && (
                    <p className="text-xs text-gray-400 leading-tight">{pageLabel}</p>
                  )}
                </div>
              </Link>
            </div>

            {/* Acciones del header */}
            <div className="flex items-center gap-1 shrink-0">

              {/* Config rápida — se ilumina con el color de marca al estar dentro de Configuración */}
              <Link
                to="/tienda/menu-config"
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                  settingsActive
                    ? "text-white"
                    : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                }`}
                style={settingsActive
                  ? { background: color, boxShadow: `0 0 0 3px ${color}33, 0 2px 10px ${color}66` }
                  : undefined}
                title="Configuración"
              >
                <Settings className="size-4" />
              </Link>

              {/* 🔧 Diagnóstico — solo superAdmin */}
              {user?.superAdmin && (
                <Link to="/admin/diagnosticos"
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                  title="Panel de diagnóstico">
                  <Activity className="size-4" />
                </Link>
              )}

              {/* 📋 Badge de tareas pendientes */}
              {tareasCount > 0 && (
                <a href="/tienda"
                  className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition-colors"
                  title={`${tareasCount} tarea${tareasCount !== 1 ? 's' : ''} pendiente${tareasCount !== 1 ? 's' : ''}`}
                >
                  <ClipboardList className="size-3.5" />
                  <span className="text-xs font-bold">{tareasCount > 99 ? "99+" : tareasCount}</span>
                </a>
              )}

              {/* 🔔 Campana de notificaciones */}
              <div className="relative" ref={modalRef}>
                <button
                  onClick={() => setOpen(v => !v)}
                  className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                    open
                      ? "text-white"
                      : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                  style={open
                    ? { background: color, boxShadow: `0 0 0 3px ${color}33, 0 2px 10px ${color}66` }
                    : undefined}
                >
                  <Bell className="size-4" />
                  {pendientes.length > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 shadow"
                      style={{ background: "#EF4444" }}
                    >
                      {pendientes.length > 9 ? "9+" : pendientes.length}
                    </span>
                  )}
                  {encuestasQr.length > 0 && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 min-w-[16px] h-[16px] text-white text-[8px] font-bold rounded-full flex items-center justify-center px-1 shadow"
                      style={{ background: "#EAB308" }}
                      title="Encuestas con QR activo"
                    >
                      {encuestasQr.length > 9 ? "9+" : encuestasQr.length}
                    </span>
                  )}
                  {notifsInternas.length > 0 && (
                    <span
                      className="absolute -top-0.5 -left-0.5 min-w-[16px] h-[16px] text-white text-[8px] font-bold rounded-full flex items-center justify-center px-1 shadow"
                      style={{ background: "#6366F1" }}
                      title="Tareas asignadas"
                    >
                      {notifsInternas.length > 9 ? "9+" : notifsInternas.length}
                    </span>
                  )}
                  {walkinEsperando.length > 0 && (
                    <span
                      className="absolute -bottom-0.5 -left-0.5 min-w-[16px] h-[16px] text-white text-[8px] font-bold rounded-full flex items-center justify-center px-1 shadow"
                      style={{ background: "#0EA5E9" }}
                      title="Clientes esperando (walk-in)"
                    >
                      {walkinEsperando.length > 9 ? "9+" : walkinEsperando.length}
                    </span>
                  )}
                </button>

                {/* Panel de notificaciones */}
                {open && (
                  <div className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    <div className="px-4 py-3 flex items-center justify-between"
                      style={{ background: `linear-gradient(135deg, ${color}15, ${color}08)` }}>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Notificaciones</p>
                        <p className="text-xs text-gray-500">
                          {pendientes.length} pendiente{pendientes.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: `${color}15` }}
                      >
                        <Bell className="size-4" style={{ color }} />
                      </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                      {pendientes.length === 0 ? (
                        <div className="px-4 py-10 text-center">
                          <div className="text-3xl mb-2">🎉</div>
                          <p className="text-sm text-gray-400">Sin notificaciones pendientes</p>
                        </div>
                      ) : (
                        pendientes.map(n => {
                          const esMesonero = n.tipo === "llamar_mesonero";
                          return (
                            <div
                              key={n.id}
                              className="px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors"
                            >
                              {/* Mesa badge */}
                              <div
                                className="mt-0.5 w-10 h-10 rounded-xl flex flex-col items-center justify-center text-white shrink-0 shadow-sm"
                                style={{ background: esMesonero ? "#F59E0B" : "#EF4444" }}
                              >
                                <span className="text-xs font-bold leading-none">
                                  {n.mesaNumero ?? "?"}
                                </span>
                                <span className="text-[9px] opacity-80">mesa</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800">
                                  {esMesonero ? "🔔 Llamar mesonero" : "📋 Solicitar cuenta"}
                                </p>
                                {n.clienteNombre && (
                                  <p className="text-xs text-gray-500 truncate">{n.clienteNombre}</p>
                                )}
                                <p className="text-xs text-gray-400">
                                  {new Date(n.createdAt).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })}
                                </p>
                              </div>
                              <div className="flex flex-col gap-1 shrink-0">
                                <button
                                  onClick={() => marcar(n.id, "visto")}
                                  className="text-[11px] px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium transition-colors"
                                >
                                  Visto
                                </button>
                                <button
                                  onClick={() => marcar(n.id, "resuelto")}
                                  className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium transition-colors"
                                >
                                  Listo ✓
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {encuestasQr.length > 0 && (
                      <div className="border-t border-gray-100">
                        <div className="px-4 py-2 bg-amber-50/60">
                          <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                            <Star className="size-3.5" /> Encuestas con QR activo
                          </p>
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                          {encuestasQr.map(e => (
                            <button
                              key={e.id}
                              onClick={() => { setEncuestaQrAbierta(e); setOpen(false); }}
                              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                            >
                              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                                <QrCode className="size-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{e.titulo}</p>
                                {e.sucursalNombre && <p className="text-xs text-gray-400 truncate">{e.sucursalNombre}</p>}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {notifsInternas.length > 0 && (
                      <div className="border-t border-gray-100">
                        <div className="px-4 py-2 bg-indigo-50/60 flex items-center justify-between">
                          <p className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                            <ClipboardList className="size-3.5" /> Tareas asignadas
                          </p>
                          <button
                            onClick={marcarTodasNotifsInternas}
                            className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5"
                            title="Marcar todas como vistas"
                          >
                            <CheckCheck className="size-3" /> Marcar todas
                          </button>
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                          {notifsInternas.map(n => (
                            <div
                              key={n.id}
                              className="px-4 py-2.5 flex items-start gap-3 hover:bg-gray-50 transition-colors"
                            >
                              <button
                                className="flex-1 min-w-0 text-left"
                                onClick={() => {
                                  marcarNotifInterna(n.id);
                                  setOpen(false);
                                  if (n.link) navigate(n.link);
                                }}
                              >
                                <p className="text-sm font-medium text-gray-800 truncate">{n.titulo}</p>
                                {n.mensaje && <p className="text-xs text-gray-500 truncate">{n.mensaje}</p>}
                                <p className="text-xs text-gray-400">
                                  {new Date(n.createdAt).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })}
                                </p>
                              </button>
                              <button
                                onClick={() => marcarNotifInterna(n.id)}
                                className="text-[11px] px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 font-medium transition-colors shrink-0"
                              >
                                Visto
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {walkinEsperando.length > 0 && (
                      <div className="border-t border-gray-100">
                        <div className="px-4 py-2 bg-sky-50/60">
                          <p className="text-xs font-semibold text-sky-700 flex items-center gap-1.5">
                            <Users2 className="size-3.5" /> Esperando mesa (walk-in)
                          </p>
                        </div>
                        <div className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                          {walkinEsperando.map(w => (
                            <button
                              key={w.id}
                              onClick={() => { navigate("/tienda/walkin"); setOpen(false); }}
                              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                            >
                              <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
                                <Users2 className="size-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {w.nombre} <span className="text-gray-400 font-normal">· {w.personas} pers.</span>
                                </p>
                                <p className="text-xs text-gray-400 truncate">{w.sucursalNombre}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-6 bg-gray-200 mx-1" />

              {/* Avatar del usuario — click para ir a Mi Perfil */}
              <a href="/perfil" className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-gray-100 transition-colors" title="Mi perfil">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                  style={{ background: `linear-gradient(135deg, ${color}, ${color}aa)` }}
                >
                  {user?.fullName?.[0]?.toUpperCase() ?? "U"}
                </div>
                <div className="hidden md:block text-right">
                  <p className="text-xs font-semibold text-gray-800 leading-tight">{user?.fullName}</p>
                  <p className="text-[10px] text-gray-400 leading-tight">
                    {ROLE_LABEL[user?.role ?? ""] ?? user?.role}
                  </p>
                </div>
              </a>

              {/* Logout */}
              <button
                onClick={() => { logout(); navigate("/login"); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                title="Cerrar sesión"
              >
                <LogOut className="size-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenido */}
      <main className="flex-1">{children}</main>

      {/* ── FOOTER PREMIUM ── */}
      <footer className="mt-auto">
        <div
          className="h-px w-full opacity-20"
          style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
        />
        <div className="bg-white/60 backdrop-blur-sm py-4 px-6">
          <div className="mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              {branding.logoUrl && (
                <img src={branding.logoUrl} alt="Logo" className="h-5 w-5 rounded-full object-cover opacity-60" />
              )}
              <span className="text-xs text-gray-400 font-medium">
                {restaurantName}
              </span>
              <span className="text-gray-200">·</span>
              <span className="text-xs text-gray-400">
                © {new Date().getFullYear()}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>Powered by</span>
              <a
                href="https://redesip.org"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-gray-500 hover:text-gray-700 transition-colors"
              >
                redesip.org
              </a>
              <span className="text-gray-200">·</span>
              <a
                href="https://redesip.org/soporte"
                target="_blank"
                rel="noreferrer"
                className="hover:text-gray-600 transition-colors"
              >
                Soporte
              </a>
              <span className="text-gray-200">·</span>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("open-help"))}
                className="hover:text-indigo-700 transition-colors inline-flex items-center gap-1"
              >
                <HelpCircle className="size-3.5" /> Ayuda
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Centro de ayuda contextual */}
      <HelpDrawer />

      {encuestaQrAbierta && (
        <EncuestaQrGrandeModal encuesta={encuestaQrAbierta} onClose={() => setEncuestaQrAbierta(null)} />
      )}
    </div>
  );
}
